import React, { useEffect, useState } from "react";
import { isValid, parseISO, parse } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableSortLabel,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import TocIcon from "@mui/icons-material/Toc";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import FieldsPicker from "./FieldsPicker";
import BarChart from "./BarChart";

type Order = "asc" | "desc";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
  numeric?: boolean;
  isDate?: boolean;
  dataType?: "string" | "number" | "date";
}

interface GenieTableProps {
  columns: Column[];
  data: any[];
  title?: string;
}

const GenieTable: React.FC<GenieTableProps> = ({ columns, data, title }) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [displayType, setDisplayType] = useState<"table" | "chart">("table");
  const [axes, setAxes] = useState({ x: "", y: "" });
  const [openFieldPicker, setOpenFieldPicker] = useState(false);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleShowPicker = () => {
    if (displayType === "table") {
      setOpenFieldPicker(true);
    } else {
      setOpenFieldPicker(false);
      setDisplayType("table");
      setPage(0);
    }
  };

  const isValidDate = (str: string) => {
    if (!str || typeof str !== "string") return false;

    // Try ISO format first (YYYY-MM-DD)
    try {
      const isoDate = parseISO(str);
      const year = isoDate.getFullYear();
      if (year < 2000 || year > new Date().getFullYear()) return false; // Basic sanity check for year

      if (isValid(isoDate)) return true;
    } catch {}

    // Try common formats
    const formats = [
      "MM/dd/yyyy", // 01/01/2023
      "dd/MM/yyyy", // 01/01/2023
      "yyyy/MM/dd", // 2023/01/01
      "MM-dd-yyyy", // 01-01-2023
      "dd-MM-yyyy", // 01-01-2023
      "yyyy-MM-dd", // 2023-01-01 (already tried above)
    ];

    for (const format of formats) {
      try {
        const parsed = parse(str, format, new Date());
        const year = parsed.getFullYear();
        if (year < 2000 || year > new Date().getFullYear()) return false; // Basic sanity check for year
        if (isValid(parsed)) return true;
      } catch {}
    }

    return false;
  };

  const sortedData = React.useMemo(() => {
    if (!orderBy) return data;

    const column = columns.find((col) => col.id === orderBy);
    if (!column) return data;

    return [...data].sort((a, b) => {
      const aValue = a[columns.findIndex((col) => col.id === orderBy)];
      const bValue = b[columns.findIndex((col) => col.id === orderBy)];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (aValue === bValue) return 0;

      let comparison = 0;

      if (!Number.isNaN(Number(aValue))) {
        // Numeric comparison
        comparison = Number(aValue) - Number(bValue);
      } else if (isValidDate(aValue)) {
        // Date comparison
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        comparison = dateA - dateB;
      } else {
        // String comparison
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return order === "asc" ? comparison : -comparison;
    });
  }, [data, order, orderBy, columns]);

  useEffect(() => {
    setPage(0);
  }, [data]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(sortedData.length / rowsPerPage) - 1, 0);
    setPage((currentPage) => Math.min(currentPage, maxPage));
  }, [rowsPerPage, sortedData.length]);

  const pagedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const row_data = sortedData.map((row) => ({
    x: row[columns.findIndex((col) => col.id === axes.x)],
    y: row[columns.findIndex((col) => col.id === axes.y)],
  }));

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        {title && (
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
            {title}            
          </Typography>

       
        )}
        {data.length > 1 && (
          <Tooltip
            title={displayType === "table" ? "Show Chart" : "Show Table"}
          >
            <IconButton onClick={handleShowPicker}>
              {displayType === "table" ? <StackedBarChartIcon /> : <TocIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {displayType === "chart" && (
        <BarChart xTitle={axes.x} yTitle={axes.y} data={row_data} />
      )}

      {displayType === "table" && (
        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || "left"}
                    style={{
                      minWidth: column.minWidth,
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedData.map((row) => {
                const rowKey = columns
                  .map((column, columnIndex) => `${column.id}:${String(row[columnIndex])}`)
                  .join("|");

                return (
                <TableRow hover role="checkbox" tabIndex={-1} key={rowKey}>
                  {columns.map((column, index) => {
                    const value = row[index];
                    let formattedValue = value;

                    // Skip parsing/formatting for "model" column
                    if (column.id === "model") {
                      formattedValue = value;
                    } else if (
                      value !== null &&
                      value !== undefined &&
                      value !== ""
                    ) {
                      // Check if the value is numeric (including strings that represent numbers)
                      const stringValue = String(value).trim();
                      formattedValue = stringValue;
                    }

                    return (
                      <TableCell key={column.id} align={column.align || "left"}>
                        {formattedValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )})}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {displayType === "table" && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {data.length > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary" }}
          >
            Returned records: {data.length}
          </Typography>
          {data.length >= 5000 && (
            <Typography
              variant="caption"
              component="div"
              sx={{ color: "warning.main", fontWeight: 600 }}
            >
              API max limit is 5000 records. Results may be truncated.
            </Typography>
          )}
        </Box>
      )}

      <FieldsPicker
        open={openFieldPicker}
        setOpen={setOpenFieldPicker}
        fields={columns.map((col) => col.id)}
        setAxes={setAxes}
        setChart={setDisplayType}
      />
    </Box>
  );
};

export default GenieTable;
